import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import Swal from 'sweetalert2';

const AlakartFamilySurvey = () => {
    const { token } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [parentUser, setParentUser] = useState(null);
    const [vetoes, setVetoes] = useState('');
    const [preferences, setPreferences] = useState('');
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        if (!token) {
            setError('Geçersiz davet linki');
            setLoading(false);
            return;
        }

        // Token'ı doğrula ve parent user bilgisini al
        const verifyToken = async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}/api/alakart-menu/verify-family-token?token=${token}`);
                if (res.data.success) {
                    setParentUser(res.data.parentUser);
                } else {
                    setError('Davet linki geçersiz veya süresi dolmuş');
                }
            } catch (err) {
                setError('Davet linki doğrulanamadı');
            } finally {
                setLoading(false);
            }
        };

        verifyToken();
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!vetoes.trim() && !preferences.trim()) {
            Swal.fire({
                title: 'Uyarı',
                text: 'En az bir veto veya tercih girmelisiniz',
                icon: 'warning'
            });
            return;
        }

        try {
            const res = await axios.post(`${API_BASE_URL}/api/alakart-menu/submit-family-input`, {
                token,
                vetoes: vetoes.split(',').map(v => v.trim()).filter(Boolean),
                preferences: preferences.split(',').map(p => p.trim()).filter(Boolean)
            });

            if (res.data.success) {
                setSubmitted(true);
                Swal.fire({
                    title: 'Teşekkürler! 🎉',
                    text: 'Tercihleriniz kaydedildi',
                    icon: 'success',
                    timer: 3000,
                    showConfirmButton: false
                });
            }
        } catch (err) {
            Swal.fire({
                title: 'Hata',
                text: 'Tercihleriniz kaydedilemedi',
                icon: 'error'
            });
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-slate-600 font-medium">Yükleniyor...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="bg-white p-8 rounded-[2.5rem] shadow-2xl max-w-md w-full text-center"
                >
                    <span className="material-symbols-outlined text-6xl text-red-500 mb-4">error</span>
                    <h2 className="text-2xl font-black text-slate-800 mb-2">Hata</h2>
                    <p className="text-slate-600 mb-6">{error}</p>
                    <button
                        onClick={() => navigate('/')}
                        className="px-6 py-3 bg-primary text-white rounded-2xl font-bold"
                    >
                        Ana Sayfaya Dön
                    </button>
                </motion.div>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center p-4">
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="bg-white p-8 rounded-[2.5rem] shadow-2xl max-w-md w-full text-center"
                >
                    <span className="material-symbols-outlined text-6xl text-green-500 mb-4">check_circle</span>
                    <h2 className="text-2xl font-black text-slate-800 mb-2">Teşekkürler! 🎉</h2>
                    <p className="text-slate-600 mb-6">Tercihleriniz başarıyla kaydedildi. {parentUser?.name || 'Plan sahibi'} bu bilgileri görecek.</p>
                    <button
                        onClick={() => navigate('/')}
                        className="px-6 py-3 bg-primary text-white rounded-2xl font-bold"
                    >
                        Ana Sayfaya Dön
                    </button>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center p-4">
            <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="bg-white p-8 rounded-[2.5rem] shadow-2xl max-w-2xl w-full"
            >
                <div className="text-center mb-8">
                    <span className="material-symbols-outlined text-6xl text-primary mb-4">restaurant_menu</span>
                    <h1 className="text-3xl font-black text-slate-800 mb-2">Aile Mutfak Anketi 🏠</h1>
                    <p className="text-slate-600">
                        {parentUser?.name || 'Bir aile üyeniz'} bu haftaki mutfak planı için senin de fikirlerini almak istiyor!
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">
                            🚫 Veto Listesi (Kesinlikle İstemediğin Yemekler/Malzemeler)
                        </label>
                        <textarea
                            value={vetoes}
                            onChange={(e) => setVetoes(e.target.value)}
                            placeholder="Örnek: patlıcan, mercimek çorbası, tavuk"
                            className="w-full p-4 border-2 border-slate-200 rounded-2xl focus:border-primary focus:outline-none resize-none"
                            rows={4}
                        />
                        <p className="text-xs text-slate-500 mt-1">Virgülle ayırarak yazabilirsin</p>
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">
                            ❤️ Tercihler (Görmek İstediğin Yemekler/Malzemeler)
                        </label>
                        <textarea
                            value={preferences}
                            onChange={(e) => setPreferences(e.target.value)}
                            placeholder="Örnek: makarna, pizza, salata"
                            className="w-full p-4 border-2 border-slate-200 rounded-2xl focus:border-primary focus:outline-none resize-none"
                            rows={4}
                        />
                        <p className="text-xs text-slate-500 mt-1">Virgülle ayırarak yazabilirsin</p>
                    </div>

                    <button
                        type="submit"
                        className="w-full py-4 bg-primary text-white rounded-2xl font-black text-lg shadow-xl shadow-primary/30 hover:scale-[1.02] transition-transform"
                    >
                        TERCİHLERİMİ KAYDET
                    </button>
                </form>

                <div className="mt-6 p-4 bg-amber-50 rounded-2xl border border-amber-100">
                    <p className="text-xs text-amber-700 font-medium">
                        💡 <strong>İpucu:</strong> Bu link 24 saat geçerlidir. Tercihlerini en kısa sürede gönder!
                    </p>
                </div>
            </motion.div>
        </div>
    );
};

export default AlakartFamilySurvey;
